import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { setAuthToken, setUser } from '../utils/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Animated Robot Component
const WavingRobot = () => (
  <div className="relative w-32 h-32 mb-6">
    <svg viewBox="0 0 100 100" className="w-full h-full">
      {/* Robot Head */}
      <rect x="25" y="20" width="50" height="40" rx="8" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2"/>
      
      {/* Robot Eyes */}
      <circle cx="38" cy="38" r="6" fill="#3B82F6">
        <animate attributeName="r" values="6;5;6" dur="2s" repeatCount="indefinite"/>
      </circle>
      <circle cx="62" cy="38" r="6" fill="#3B82F6">
        <animate attributeName="r" values="6;5;6" dur="2s" repeatCount="indefinite"/>
      </circle>
      
      {/* Robot Smile */}
      <path d="M 38 50 Q 50 58 62 50" stroke="#3B82F6" strokeWidth="3" fill="none" strokeLinecap="round"/>
      
      {/* Robot Antenna */}
      <line x1="50" y1="20" x2="50" y2="10" stroke="#e2e8f0" strokeWidth="3"/>
      <circle cx="50" cy="8" r="4" fill="#3B82F6">
        <animate attributeName="fill" values="#3B82F6;#60A5FA;#3B82F6" dur="1s" repeatCount="indefinite"/>
      </circle>
      
      {/* Robot Body */}
      <rect x="30" y="62" width="40" height="25" rx="5" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2"/>
      
      {/* Waving Arm */}
      <g className="origin-bottom-left">
        <animateTransform
          attributeName="transform"
          type="rotate"
          values="-10;20;-10"
          dur="0.8s"
          repeatCount="indefinite"
        />
        <rect x="70" y="65" width="20" height="8" rx="4" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 70 69"
            to="30 70 69"
            dur="0.4s"
            repeatCount="indefinite"
            additive="sum"
          />
        </rect>
        {/* Hand */}
        <circle cx="92" cy="69" r="6" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2">
          <animate attributeName="cx" values="92;98;92" dur="0.8s" repeatCount="indefinite"/>
          <animate attributeName="cy" values="69;60;69" dur="0.8s" repeatCount="indefinite"/>
        </circle>
      </g>
      
      {/* Left Arm */}
      <rect x="10" y="65" width="20" height="8" rx="4" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2"/>
    </svg>
    
    {/* Hi Text Bubble */}
    <div className="absolute -top-2 -right-4 bg-white rounded-full px-3 py-1 shadow-lg border border-emerald-100 animate-bounce">
      <span className="text-blue-600 font-bold text-sm">Hi Rakshith!</span>
    </div>
  </div>
);

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', formData);
      setAuthToken(response.data.access_token);
      setUser(response.data.user);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1751644332113-2004a1b143f1?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-700/90 to-green-500/80" />
        <div className="relative z-10 flex flex-col justify-center items-center text-white p-12">
          {/* Animated Robot */}
          <WavingRobot />
          
          {/* Brand Name - White and Bold */}
          <h1 className="text-5xl font-bold font-outfit mb-4 text-white drop-shadow-lg">
            Content Work
          </h1>
          <p className="text-xl text-center text-white/90">Streamline Your Content Creation Workflow</p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center lg:text-left">
            <h2 className="text-4xl font-bold font-outfit text-slate-900 mb-2">Welcome Back</h2>
            <p className="text-slate-600">Sign in to access your dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 bg-slate-50 border-slate-200 focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="your@email.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-slate-50 border-slate-200 focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 pr-10"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white h-11 text-base font-semibold shadow-lg shadow-primary/20 hover:-translate-y-0.5 transition-all"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
